import React, { useState, useEffect, useMemo } from 'react';
import {  useTheme, useMediaQuery, Box, Grid, Typography, TextField, Button, styled, MenuItem } from '@mui/material';

import BaseCodeSelectbox from '../../common/form/selectbox/BaseCodeSelectbox';
import DetailTemplate from '../../common/template/DetailTemplate';
import PartnerModalConatiner from '../../../containers/common/form/modal/PartnerModalConatiner';
import FormLabel from '../../common/form/FormLabel';
import EmpModalContainer from '../../../containers/common/form/modal/EmpModalContainer';
import { eachMonthOfInterval, endOfMonth, format, addMonths, set, lastDayOfMonth, isBefore, parseISO } from 'date-fns';
import koLocale from 'date-fns/locale/ko';
import DateAdapter from '@mui/lab/AdapterDateFns';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import DatePicker from '@mui/lab/DatePicker';
import InvoiceInfoForm from './InvoiceInfoForm';

const ForecastBaseInfoForm = ({ formik, onSelectedProducts, titleName, isLocked, handleLockClick, hideLock, updateNot}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [expDay, setExpDay] = useState(null);
  const onChangeDate = (newValue, name) => {
    formik.setFieldValue(name, newValue);
  };

  const [openParnter, setOpenParnter] = useState(false);
  const handleOpen = () => {
    setOpenParnter(true);
  };
  const handleClose = () => {
    setOpenParnter(false);
  };

  const selectedPartner = (partner) => {
    formik.setFieldValue('partnerInc', partner.partnerNm);
  };

  const handleOpenClient = () => {
    setOpenClient(true);
  };
  const handleCloseClient = () => {
    setOpenClient(false);
  };

  const [openClient, setOpenClient] = useState(false);
  const [openVendor, setOpenVendor] = useState(false);
  const handleOpenVendor = () => {
    setOpenVendor(true);
  };
  const handleCloseVendor = () => {
    setOpenVendor(false);
  };

  const selectedClient = (partner) => {
    formik.setFieldValue('clientCmp', partner.partnerUuid);
    formik.setFieldValue('clientCmpNm', partner.partnerNm);
    formik.setFieldValue('incClientTyp', partner.incTyp);
  };

  const selectedVendor = (partner) => {
    formik.setFieldValue('vendorCmp', partner.partnerUuid);
    formik.setFieldValue('vendorCmpNm', partner.partnerNm);
    formik.setFieldValue('partnerTyp', partner.partnerTyp);
  };

  const [openEmp, setOpenEmp] = useState(false);
  const [title, setTitle] = useState('사원찾기');

  const openEmpModal = (type, title) => {
    setTitle(title);
    setOpenEmp(true);
  };
  const handleEmpClose = () => {
    setOpenEmp(false);
  };

  const selectedEmployee = (emp) => {
    formik.setFieldValue(`crtNm`, emp.usrNm, true);
    formik.setFieldValue(`crtUsr`, emp.usrUuid, false);
  };

  const renderDatePicker = (fieldName, label, requiredText, customWidth = '100%') => (
    <LocalizationProvider dateAdapter={DateAdapter} locale={koLocale}>
      <DatePicker
        inputFormat="yyyy-MM-dd"
        mask="____-__-__"
        value={formik.getFieldProps(fieldName).value || null}
        onChange={(newValue) => formik.setFieldValue(fieldName, newValue)}
        readOnly={isLocked || (fieldName !== 'expDay' && updateNot)}
        renderInput={(params) => {
          const { inputProps, InputProps, ...otherParams } = params;
          return (
            <TextField
              {...otherParams}
              fullWidth
              size="small"
              variant="outlined"
              label={getMobileLabel(label, requiredText)}
              InputLabelProps={{ ...mobileInputLabelProps, shrink: true }}
              inputProps={{ ...inputProps, value: inputProps.value || '', readOnly: isLocked }}
              InputProps={InputProps}
              error={formik.getFieldMeta(fieldName).touched  && Boolean(formik.getFieldMeta(fieldName).error)}
              helperText={formik.getFieldMeta(fieldName).touched && formik.getFieldMeta(fieldName).error}
              sx={{ 
                width: '100%',
                '& .MuiInputBase-root': { 
                  maxWidth: isMobile ? 'none' : customWidth,
                  // 아이콘이 있는 오른쪽 영역에 패딩을 줌
                  pr: '8px', 
                },
                '& .MuiInputBase-input': {
                  // 💡 가장 중요한 부분: 아이콘 너비만큼(약 30~35px) 오른쪽 패딩을 강제로 확보
                  paddingRight: '32px !important', 
                  // 텍스트가 너무 길면 잘리지 않고 생략되거나 다음 줄로 넘어가지 않도록 설정
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                },
                // 만약 아이콘 크기 자체가 문제라면 아이콘 위치 조정
                '& .MuiInputAdornment-root': {
                  marginLeft: '-28px', // 아이콘을 조금 더 오른쪽 끝으로 밀어냄
                  position: 'relative',
                  zIndex: 1,
                }
              }}
            />
          );
        }}
      />
    </LocalizationProvider>
  );

  // 1. 제공해주신 커스텀 FormLabel 스타일 정의
  const FormLabel = styled(Box)(({ theme }) => ({
    ...theme.typography.subtitle2,
    textAlign: 'left',   
    paddingLeft: '30px', 
    display: 'flex',     
    alignItems: 'center',
    height: '100%',      
    fontSize: '0.875rem',
    fontWeight: 600, // 가독성을 위해 굵기 추가
  }));

  // 모바일용 커스텀 라벨 (단어별 색상)
  const getMobileLabel = (text, requiredText = '(필수)') => isMobile ? (
    <span>
      <span style={{ fontSize: '0.9rem' }}>{text}</span>{' '}
      <span style={{  }}>{requiredText}</span>
    </span>
  ) : undefined;

  // 모바일용 InputLabelProps
  const mobileInputLabelProps = isMobile ? {
    shrink: true,
    sx: {
      fontSize: '0.7rem',
      transform: 'translate(-11px, -9px) scale(1)', 
      color: 'primary.main',
      backgroundColor: 'white', // 라벨 뒤 선 가리기용
    },
  } : {};

  useEffect(() => {
    if (formik.values.expDay) {
        setExpDay(formik.values.expDay);
    }
   
  });
  
 const fieldConfigs = [
    { name: 'prjTyp', label: '매출 타입', type: 'select', slcCd: 'PRJ_TYP' },
    { name: 'prjImpt', label: '프로젝트 중요도', type: 'select', slcCd: 'PRJ_IMPT' },
    { name: 'bugetYn', label: '예산여부', type: 'select', slcCd: 'BUGET_YN' },
    { name: 'cstNm', label: '사업명', type: 'text' },
    { name: 'productYn', label: '제품여부', type: 'select', slcCd: 'PRD_YN' },
    { name: 'expDay', label: '매출 예상일', type: 'date' },
    { 
      name: 'clientCmpNm', 
      label: '고객사', 
      type: 'text', 
      hasButton: true, 
      onClick: handleOpenClient, 
      buttonLabel: '고객사 선택',
      readOnly: true
    },
    { name: 'incClientTyp', label: '고객 유형', type: 'text', readOnly: true, requiredText: '(자동)' },
    { name: 'customerCf', label: '고객 구분', type: 'select', slcCd: 'CLI_TYP' },
    { 
      name: 'vendorCmpNm', 
      label: '매출처', 
      type: 'text', 
      hasButton: true, 
      onClick: handleOpenVendor, 
      buttonLabel: '매출처 선택',
      readOnly: true
    },
    { 
      name: 'partnerTyp', 
      label: '매출처 유형', 
      type: 'select', 
      blcCd: 'BASE', 
      mlcCd: 'PARTNER', 
      slcCd: 'PARTNER_TYP' 
    },
    { name: 'salesTyp', label: '영업 유형', type: 'select', slcCd: 'SAL_TYP' },
    { 
      name: 'crtNm', 
      label: '영업 담당자', 
      type: 'text', 
      readOnly: true, 
      hasButton: true, 
      onClick: () => openEmpModal('', '영업담당자 선택'),
      buttonLabel: '영업담당자 선택' 
    },
    { 
      name: 'procurement', 
      label: '조달여부', 
      type: 'nativeSelect', 
      requiredText: '(선택)',
      options: [{ value: 'Y', label: 'Y' }, { value: 'N', label: 'N' }] 
    },
  ];

  return (
    <Box>
      <DetailTemplate title={titleName || "포캐스팅 등록"} isLocked={isLocked} handleLockClick={handleLockClick} hideLock>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexDirection: 'column',
            border: (theme) => `1px solid ${theme.palette.divider}`,
            bgcolor: 'background.paper',
            p: 1,
            // 모든 하위 TextField에 적용되도록 더 구체적인 선택자 사용
            '& .MuiInputBase-input': {
              fontSize: '15px', // 입력 텍스트 크기
              padding: '8px',   // 높이가 너무 답답해 보이면 패딩으로 조절
            },
            '& .MuiInputLabel-root': {
              
            ml: 3,
              fontSize: '13px', // 라벨 텍스트 크기
            }
          }}
        >
          <Grid container spacing={1} alignItems="center" sx={{py: 1,}}>
            {fieldConfigs.map((field, index) => (
              <React.Fragment key={field.name}>
                
                {/* [데스크탑] 라벨: 모바일이 아닐 때만 원래 레이아웃대로 1.5 차지 */}
                {!isMobile && (
                  <Grid item xs={1.5}>
                    <FormLabel >
                      {field.label}
                      <Typography variant="subtitle2" component="span" sx={{ mx: 1, color: 'error.main' }}>
                        {field.requiredText || '(필수)'}
                      </Typography>
                    </FormLabel>
                  </Grid>
                )}

                {/* [공통] 입력창 영역: 데스크탑은 원래 지정된 너비, 모바일은 12 차지 */}
                <Grid item xs={isMobile ? 12 : (field.isFullWidth ? 10.5 : field.hasButton ? 2.5 : 2.5)}>
                  <Grid container spacing={1} alignItems="center">
                    
                    {/* 입력 필드 본체 */}
                    <Grid item xs={field.hasButton ? (isMobile ? 8.5 : 9) : 12}
                      sx={{
                        // 모바일일 때만 아래로 2단위(약 16px) 간격을 줍니다.
                        mb: isMobile ? 1.5 : 0 
                      }}
                    >
                      {field.type === 'select' && (
                        <BaseCodeSelectbox
                          formik={formik}
                          filterObject={{ blcCd: field.blcCd || 'BASE', mlcCd: field.mlcCd || 'FORCAST', slcCd: field.slcCd, useYn: 'Y' }}
                          name={field.name}
                          isLabelTop={isMobile} // 모바일일 때만 상단 고정 활성화
                          isLocked={isLocked}
                          label={getMobileLabel(field.label, field.requiredText)}
                        />
                      )}

                      {field.type === 'nativeSelect' && (
                        <TextField
                          select fullWidth size="small" name={field.name}
                          value={formik.values[field.name] || 'N'}
                          onChange={formik.handleChange}
                          label={getMobileLabel(field.label, field.requiredText)}
                          InputLabelProps={mobileInputLabelProps}
                          InputProps={{ readOnly: field.readOnly || isLocked }}
                        >
                          {field.options.map((opt) => <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>)}
                        </TextField>
                      )}

                      {(field.type === 'text') && (
                        <TextField
                          fullWidth size="small"
                          multiline={field.multiline}
                          rows={field.rows}
                          name={field.name}
                          value={formik.values[field.name]}
                          onChange={formik.handleChange}
                          label={getMobileLabel(field.label, field.requiredText)}
                          InputLabelProps={mobileInputLabelProps}
                          InputProps={{ readOnly: field.readOnly || isLocked }}
                          error={formik.touched[field.name] && Boolean(formik.errors[field.name])}
                          helperText={formik.touched[field.name] && formik.errors[field.name]}
                        />
                      )}

                      {/* 3. 날짜 선택 타입 (원래 설정 유지 + 모바일 대응) */}
                      {field.type === 'date' && renderDatePicker(field.name, field.label, field.requiredText)}
                    </Grid>

                    {/* [공통] 버튼 영역 (고객사/매출처/영업담당자 선택) */}
                    {field.hasButton && (
                      <Grid item xs={isMobile ? 3.5 : 3}>
                        <Button
                          fullWidth
                          variant="outlined"
                          // 영업담당자 필드일 때만 'primary'(파란색), 나머지는 기존 'secondary' 유지
                          color={field.name === 'crtNm' ? 'primary' : 'secondary'} 
                          size="small"
                          onClick={field.onClick}
                          disabled={isLocked}
                          sx={{ 
                            height: isMobile ? '38px' : 'auto', 
                            mt: isMobile ? '-16px' : 'auto', 
                            ml: isMobile ? '-5px' : 'auto', 
                            whiteSpace: 'nowrap', 
                            width: '90px' 
                          }}
                        >
                          {isMobile ? '선택' : (field.buttonLabel || '선택')}
                        </Button>
                      </Grid>
                    )}
                  </Grid>
                </Grid>

               {!isMobile && ((index + 1) % 3 === 0 || index === fieldConfigs.length - 1) && (
                <Grid item xs={12}>
                  <Box 
                    sx={{ 
                      borderBottom: (theme) => `1px solid ${theme.palette.divider}`, 
                      width: '100%' 
                    }} 
                  />
                </Grid>
              )}
              </React.Fragment>
            ))}
            
          </Grid>
          <InvoiceInfoForm formik={formik} prjTyp={formik.values.prjTyp} renderDatePicker = {renderDatePicker} getMobileLabel mobileInputLabelProps isLocked={isLocked} updateNot={updateNot} />
          <Grid container spacing={1} sx={{ py: 1 }}>
            <Grid item xs={12}>
              <Grid container spacing={1}>
                {!isMobile && (
                  <Grid item xs={1.5}> {/* 멀티라인이므로 라벨 위치 살짝 조정 */}
                    <FormLabel >참고사항                  
                    </FormLabel>
                  </Grid>
                )}
                <Grid item xs={isMobile ? 12 : 10.5}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    name="forcastTxt"
                    value={formik.values.forcastTxt}
                    onChange={formik.handleChange}
                    label={getMobileLabel('참고사항', '(선택)')} // 모바일 전용 라벨
                    InputLabelProps={mobileInputLabelProps}
                    InputProps={{ readOnly: isLocked }}
                    placeholder="참고사항을 입력해주세요."
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>
        {openParnter && (
          <PartnerModalConatiner
            link
            title="고객명 선택"
            open={openParnter}
            handleClose={handleClose}
            selectedPartner={selectedPartner}
          />
        )}
         {openClient && (
          <PartnerModalConatiner
            link
            title="고객사 선택"
            open={openClient}
            handleClose={handleCloseClient}
            selectedPartner={selectedClient}
            selectedPartnerTyp={'고객사'}
          />
        )}
        {openVendor && (
          <PartnerModalConatiner
            link
            title="매출처 선택"
            open={openVendor}
            handleClose={handleCloseVendor}
            selectedPartner={selectedVendor}
            selectedPartnerTyp={'파트너'}
          />
        )}
        {openEmp && (
          <EmpModalContainer
            title={title}
            multi={false}
            open={openEmp}
            roleTyp="SALES"
            handleClose={handleEmpClose}
            selectedEmployee={selectedEmployee}
          />
        )}
      </DetailTemplate>
    </Box>
  );
};

export default ForecastBaseInfoForm;
